import React from 'react';
import PropTypes from 'prop-types';

import { snakeCase } from 'lodash';

import highlightText from 'v-c/Text/HighlightText';

import EmptyResults from 'v-c/EmptyPageViews/ReportsEmptyResults';
import { FormattedHTMLMessage } from 'component-utilities/intl';

import LoadingIndicator from 'v-c/LoadingIndicator';
import JobStatusCell from 'v-c/TableView/JobStatusCell';
import NewButton from 'v-c/Templates/Audits/NewButton';
import Search from 'v-c/Templates/Audits/Search';
import StandaloneContainer from 'v-c/Templates/Audits/StandaloneContainer';
import FormErrorText from 'v-c/Forms/FormErrorText';

import {
    TableView,
    TableHeader,
    TableBody,
    TableRow,
    SortCell,
    DataCell,
    UserCell,
    TextCell,
} from 'v-c/TableView';

const { sortKeys: { UNSORTED } } = SortCell;

export default function AuditsTableView(props) {
    const {
        sortKey,
        sortOrder,
        reports,
        searchString,
        visibleColumns,
        getMessage,
        doSort,
        setFilter,
        setVisibleColumns,
        download,
        uiRoutes,
        configs,
        isLoading,
        error,
        newReportUrl,
        browserHistory,
        prefix,
    } = props;

    const { COLUMNS, DISABLED_COLUMNS } = configs.AUDITING;
    const {
        NAME,
        STATUS,
        REQUESTED_DATE,
        REQUESTED_BY,
        DATE_RANGE,
    } = COLUMNS;

    const highlightIfSearching = text => (
        searchString.length ? highlightText(text, [searchString]) : text
    );

    const columnValues = Object.values(COLUMNS);

    const columnSelector = {
        activeColumns: visibleColumns,
        allColumns: columnValues,
        disabledColumns: DISABLED_COLUMNS,
        localizedNames: columnValues.reduce((all, id) => (
            { ...all, [id]: getMessage(`${prefix}.reports.columns.${snakeCase(id)}`) }
        ), {}),
        onChange: setVisibleColumns,
    };

    const isView = prefix === 'app';

    // TODO: something with these styles once we make another one of these!
    return (
        <div style={{ marginTop: '10px', position: 'relative' }} className="audits">
            <StandaloneContainer>
                <NewButton
                    browserHistory={browserHistory}
                    actionHelper={{
                        new: { path: newReportUrl },
                    }}
                    onClick
                />
                <Search
                    searchHelper={{
                        value: searchString,
                        onChange: setFilter,
                    }}
                />
            </StandaloneContainer>
            {!!(isLoading && !reports.length) && (
                <EmptyResults>
                    <FormattedHTMLMessage id="pcc.reports.states.loading" />
                </EmptyResults>
            )}

            {!!(error && !isLoading) && (
                <EmptyResults>
                    <FormErrorText error="pcc.reports.states.error" field="" />
                </EmptyResults>
            )}

            {!!(!reports.length && !searchString.length && !isLoading && !error) && (
                <EmptyResults>
                    <FormattedHTMLMessage id={isView ? 'app.empty_results.audits' : 'pcc.reports.states.empty_results'} />
                </EmptyResults>
            )}

            {!!(!reports.length && searchString.length && !isLoading && !error) && (
                <EmptyResults>
                    <FormattedHTMLMessage id={isView ? 'app.no_results.audits' : 'pcc.reports.states.no_results'} />
                </EmptyResults>
            )}

            {!!(!error && reports.length > 0) && (
                <TableView tableId="audits">
                    <TableHeader
                        columnSelector={columnSelector}
                    >
                        <TableRow
                            key={0}
                            index={0}
                            rowId="header"
                        >
                            {visibleColumns.map((cellId, idx) => {
                                let newSortKey = UNSORTED; // change name
                                newSortKey = sortKey === cellId ? sortOrder : newSortKey;

                                const column = {
                                    onClick: doSort,
                                    sortKey: newSortKey,
                                    key: idx,
                                    text: getMessage(`${prefix}.reports.columns.${snakeCase(cellId)}`),
                                    uiroutes: uiRoutes,
                                    cellId,
                                };

                                return <SortCell {...column} />;
                            })}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {reports.map((report, rowIdx) => (
                            <TableRow
                                key={report.id}
                                index={rowIdx}
                                rowId={report.id}
                            >
                                {visibleColumns.map((cellId) => {
                                    const { jobName, requestedDate, requester, dateRange, status } = report;

                                    switch (cellId) {
                                    case STATUS: {
                                        return (
                                            <DataCell
                                                key={cellId}
                                                cellId={STATUS}
                                                data-job-status={status}
                                                isFloatingCell
                                            >
                                                <JobStatusCell
                                                    {...report}
                                                    downloadAction={download}
                                                    finishedStates={configs.JOB_STATUSES.FINISHED}
                                                    prefix={prefix}
                                                />
                                            </DataCell>
                                        );
                                    }
                                    case NAME: {
                                        return (
                                            <TextCell
                                                key={cellId}
                                                cellId={NAME}
                                            >
                                                {highlightIfSearching(jobName)}
                                            </TextCell>
                                        );
                                    }
                                    case REQUESTED_DATE: {
                                        return (
                                            <TextCell
                                                key={cellId}
                                                cellId={REQUESTED_DATE}
                                            >
                                                {highlightIfSearching(requestedDate)}
                                            </TextCell>
                                        );
                                    }
                                    case REQUESTED_BY: {
                                        if (requester) {
                                            return (
                                                <UserCell
                                                    key={cellId}
                                                    cellId={REQUESTED_BY}
                                                    userName={highlightIfSearching(requester)}
                                                />
                                            );
                                        }

                                        return (
                                            <DataCell
                                                key={cellId}
                                                cellId={REQUESTED_BY}
                                            >
                                                <div className="loading-cell">
                                                    <LoadingIndicator orientation="left" text="" isLoading />
                                                </div>
                                            </DataCell>
                                        );
                                    }
                                    case DATE_RANGE: {
                                        return (
                                            <TextCell
                                                key={cellId}
                                                cellId={DATE_RANGE}
                                            >
                                                {highlightIfSearching(dateRange)}
                                            </TextCell>
                                        );
                                    }
                                    default: { return null; }
                                    }
                                })}
                            </TableRow>
                        ))}
                    </TableBody>
                </TableView>
            )}
        </div>
    );
}

AuditsTableView.propTypes = {
    doSort: PropTypes.func.isRequired,
    download: PropTypes.func.isRequired,
    getMessage: PropTypes.func.isRequired,
    reports: PropTypes.array.isRequired,
    searchString: PropTypes.string.isRequired,
    setFilter: PropTypes.func.isRequired,
    setVisibleColumns: PropTypes.func.isRequired,
    sortKey: PropTypes.string.isRequired,
    sortOrder: PropTypes.string.isRequired,
    visibleColumns: PropTypes.array.isRequired,
    configs: PropTypes.object.isRequired,
    uiRoutes: PropTypes.object.isRequired,
    isLoading: PropTypes.bool.isRequired,
    error: PropTypes.bool.isRequired,
    newReportUrl: PropTypes.string.isRequired,
    browserHistory: PropTypes.object.isRequired,
    prefix: PropTypes.string.isRequired,
};

AuditsTableView.defaultProps = {
    isLoading: false,
    error: false,
    uiroutes: null,
};
